/* =============================================================================
   VIEWS / ADMIN / ADMIN-ORDER-ENTRY-DINEIN — Hier-Essen 3-Step-Flow + Walk-In
   =============================================================================
   v0.76.0-beta / Session 70 / REF-5 Cut 3/4
   ----------------------------------------------------------------------------
   Hier-Essen-Flow: Step 2 (Gast-Daten) → Step 3 (Tisch-Vorschlag) → Step 4
   (Decision: jetzt bestellen ODER erst Platz nehmen). „Erst Platz nehmen"
   → submitDineInSeatOnly = Walk-In-only. „Jetzt bestellen" → Step 5 (Items).

   Cross-Module:
     - _state via ESM live binding aus admin-order-entry-state.js
     - getState() ditto
     - Re-Render des Modals via window.renderAdminOrderEntryModal()
     - Modal-Close via window.closeAdminOrderEntry()
     - Legacy-Stubs für alte Inline-Handler / Tests
============================================================================ */

import { htmlEscape, showToast, todayISO } from '../../utils/helpers.js';
import { _state, getState } from './admin-order-entry-state.js';
import { renderMailOptInBlock, ORDER_MAIL_KINDS } from './mail-optin-block.js';
// S134 P2 — Tisch-Step nutzt den Reservierungs-Assignment-Picker (Single-Source).
import { buildAssignmentPickerHtml, setWalkInPickerState } from '../assignment-picker.js';

function rerender() {
  if (typeof window.renderAdminOrderEntryModal === 'function') {
    window.renderAdminOrderEntryModal();
  }
}

/** Step 2 (Hier-Essen) — Gast-Daten erfassen, BEVOR ein Tisch gewählt wird. */
export function renderStepDineInData() {
  const f = _state.dineInForm;
  const customerInfo = f.customerId ? `
    <div style="background:var(--green-soft);border-left:3px solid var(--green);padding:8px 12px;margin:6px 0;font-size:.88rem;">
      ✓ Stammkunde erkannt — Datenbank-Verknüpfung wird gespeichert.
    </div>` : '';
  return `
    <div class="modal-header">
      <h2>Bestellung aufnehmen — 🍽️ Walk-In</h2>
      <button class="modal-close" onclick="closeAdminOrderEntry()" aria-label="Schließen">×</button>
    </div>
    <p style="color: var(--ink-soft); font-size: .92rem; margin-bottom: 14px;">
      <strong>Schritt 1 von 3:</strong> Gast-Daten erfassen. Das System schlägt im nächsten Schritt den passenden Tisch vor.
    </p>
    <div class="admin-order-entry-body">
      <div class="field-row">
        <div class="field" style="flex:1;">
          <label>Anzahl Gäste *</label>
          <input type="number" id="din-guests" min="1" max="20" value="${f.guests}"
                 oninput="adminOrderSetDineInField('guests', this.value)" required autofocus>
        </div>
        <div class="field" style="flex:1;">
          <label>Geplante Verweildauer (Min.) *</label>
          <input type="number" id="din-duration" min="15" max="480" step="15" value="${f.durationMin}"
                 oninput="adminOrderSetDineInField('durationMin', this.value)" required>
        </div>
      </div>
      <div class="field-row">
        <div class="field" style="flex:1;">
          <label>Name (optional)</label>
          <input type="text" id="din-name" value="${htmlEscape(f.guestName)}"
                 oninput="adminOrderSetDineInField('guestName', this.value)" placeholder="z.B. Müller">
        </div>
        <div class="field" style="flex:1;">
          <label>Telefon (optional, Stammkunden-Abgleich)</label>
          <input type="tel" id="din-phone" value="${htmlEscape(f.guestPhone)}"
                 oninput="adminOrderSetDineInField('guestPhone', this.value)"
                 onblur="adminOrderDineInLookupCustomer()" placeholder="+49 …">
        </div>
      </div>
      ${customerInfo}
      <div class="field">
        <label>Bemerkung (optional)</label>
        <input type="text" id="din-notes" value="${htmlEscape(f.notes)}"
               oninput="adminOrderSetDineInField('notes', this.value)" placeholder="z.B. Stammgast, Allergien, Geburtstag …">
      </div>
      ${renderMailOptInBlock({
        kinds: ORDER_MAIL_KINDS,
        email: _state.customerEmail,
        optIn: _state.mailOptIn,
        idPrefix: 'admorder',
        emailHandler: 'adminOrderSetCustomerEmail',
        toggleHandler: 'adminOrderSetMailOptIn',
        showToggles: false  // S88 C: keine Checkboxen — Server leitet Opt-in aus E-Mail ab
      })}
    </div>
    <div class="admin-order-foot">
      <button class="btn btn-ghost" onclick="adminOrderBackToService()">← Zurück</button>
      <button class="btn btn-primary" onclick="adminOrderDineInProceed()">Tisch vorschlagen →</button>
    </div>
  `;
}

/** Walk-In-Picker-Kontext aus dem dineInForm bauen (heute · jetzt · Dauer). */
function buildWalkInCtx() {
  const f = _state.dineInForm;
  const now = new Date();
  const nowTime = String(now.getHours()).padStart(2, '0') + ':' + String(now.getMinutes()).padStart(2, '0');
  return {
    id: 'walkin-pick',                 // synthetische ID (nicht in reservations)
    date: todayISO(),
    time: nowTime,
    guests: Math.max(1, Number(f.guests) || 1),
    area: 'any',
    durationMin: Math.max(15, Number(f.durationMin) || 90),
    name: f.guestName || '',
    notes: f.notes || '',
    // S197 — Vorauswahl-Fix (Jonas): ein aus der Belegung direkt gewählter Tisch
    // (startWalkInAtTable → adminOrderSetTable setzt nur _state.tableId) muss im
    // Tisch-Picker (Step 3) VORAUSGEWÄHLT erscheinen. Bisher las buildWalkInCtx nur
    // _state.assignedTableIds (leer) → Picker startete ohne Markierung, Operator
    // musste den Tisch erneut wählen. Fallback auf _state.tableId → vorausgewählt,
    // Operator bestätigt nur noch (Konflikt-Pre-Check/Sharing bleiben erhalten).
    assignedTables: (_state.assignedTableIds && _state.assignedTableIds.length)
      ? _state.assignedTableIds.slice()
      : (_state.tableId ? [_state.tableId] : [])
  };
}

/** Picker-State für Walk-In sicherstellen (idempotent über Re-Renders). */
function ensureWalkInPickerState() {
  const ps = (typeof window !== 'undefined') ? window._pickerState : null;
  if (!ps || !ps.walkIn) {
    setWalkInPickerState(buildWalkInCtx());
  }
}

/** Step 3 (Hier-Essen) — Tisch-Wahl über den Reservierungs-Assignment-Picker
 *  (S134 P2, Single-Source). Der Picker rendert in dasselbe #modal-content;
 *  Accept committet über `commitWalkInTableAssignment`, „← Zurück" über
 *  `adminOrderWalkInPickerBack`. Die alte S60-Chip-/Score-Logik entfällt —
 *  Mehr-Tisch-Kombis, Connectivity-Check und Konflikt-freie Vorschläge kommen
 *  jetzt aus der gleichen Engine wie bei den Reservierungen. */
export function renderStepDineInTable() {
  ensureWalkInPickerState();
  return buildAssignmentPickerHtml();
}

/** Step 4 (Hier-Essen) — Decision: jetzt bestellen ODER erst Platz nehmen. */
export function renderStepDineInDecision() {
  const state = getState();
  const f = _state.dineInForm;
  // S134 P2 — Mehr-Tisch-Kombis: assignedTableIds ist die Wahrheit, tableId
  // der primäre Anker. Namen aller gewählten Tische zeigen.
  const ids = (_state.assignedTableIds && _state.assignedTableIds.length)
    ? _state.assignedTableIds : (_state.tableId ? [_state.tableId] : []);
  const tableName = ids.map(id => {
    const t = (state.tables || []).find(x => x.id === id);
    return (t && t.name) || id;
  }).join(' + ') || '—';
  return `
    <div class="modal-header">
      <h2>Bestellung aufnehmen — Tisch ${htmlEscape(String(tableName))}</h2>
      <button class="modal-close" onclick="closeAdminOrderEntry()" aria-label="Schließen">×</button>
    </div>
    <p style="color: var(--ink-soft); font-size: .92rem; margin-bottom: 14px;">
      <strong>Schritt 3 von 3:</strong> Will der Gast direkt bestellen, oder erstmal nur Platz nehmen?
    </p>
    <div class="admin-order-summary" style="background:var(--bg-soft,#fafaf7);padding:10px 14px;border-radius:8px;margin-bottom:14px;font-size:.9rem;">
      <strong>Tisch ${htmlEscape(String(tableName))}</strong> · ${f.guests} Gäste · ${f.durationMin} Min Verweildauer
      ${f.guestName ? ` · ${htmlEscape(f.guestName)}` : ''}
      ${f.customerId ? ' · ✓ Stammkunde' : ''}
    </div>
    <div class="admin-order-svc-grid" style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
      <button class="admin-order-svc-tile" onclick="adminOrderDineInProceedToOrder()"
              style="padding:24px 16px;border-radius:10px;background:var(--info-soft);border:2px solid var(--info);color:var(--info);text-align:left;cursor:pointer;">
        <div style="font-size:1.4rem;">📋</div>
        <div style="font-weight:600;margin-top:6px;">Jetzt Bestellung aufnehmen</div>
        <div style="font-size:.85rem;color:var(--ink);margin-top:4px;">
          Items wählen, an Küche/Bar senden. Tisch wird zusammen mit der Bestellung belegt.
        </div>
      </button>
      <button class="admin-order-svc-tile" onclick="submitDineInSeatOnly()"
              style="padding:24px 16px;border-radius:10px;background:var(--warn-soft);border:2px solid var(--accent);color:var(--accent-dark);text-align:left;cursor:pointer;">
        <div style="font-size:1.4rem;">🪑</div>
        <div style="font-weight:600;margin-top:6px;">Erst Platz nehmen</div>
        <div style="font-size:.85rem;color:var(--ink-soft);margin-top:4px;">
          Tisch wird belegt, Bestellung später aufnehmen. Reservierungs-Verfügbarkeit wird reduziert.
        </div>
      </button>
    </div>
    <div class="admin-order-foot" style="margin-top:16px;">
      <button class="btn btn-ghost" onclick="adminOrderBackToDineInTable()">← Tisch ändern</button>
    </div>
  `;
}

// ----------------------------------------------------------------------------
// Setter + Navigation für Hier-Essen-Flow
// ----------------------------------------------------------------------------

export function adminOrderSetDineInField(field, value) {
  if (!Object.prototype.hasOwnProperty.call(_state.dineInForm, field)) return;
  if (field === 'guests' || field === 'durationMin') {
    _state.dineInForm[field] = Math.max(1, parseInt(value, 10) || 1);
  } else {
    _state.dineInForm[field] = String(value || '');
    if (field === 'guestPhone' && _state.dineInForm.customerId) {
      _state.dineInForm.customerId = null;
    }
  }
}

export async function adminOrderDineInLookupCustomer() {
  const phone = (_state.dineInForm.guestPhone || '').trim();
  if (!phone || phone.length < 4) return;
  if (!window.App || !window.App.storage || !window.App.storage.customers
      || typeof window.App.storage.customers.findByPhone !== 'function') return;
  try {
    const customer = await window.App.storage.customers.findByPhone(phone);
    if (customer && customer.id) {
      _state.dineInForm.customerId = customer.id;
      if (!_state.dineInForm.guestName && customer.name) {
        _state.dineInForm.guestName = customer.name;
      }
      if (_state.step === 2) rerender();
    }
  } catch (err) {
    console.warn('[dine-in-lookup]', err);
  }
}

export function adminOrderDineInProceed() {
  const f = _state.dineInForm;
  if (!f.guests || f.guests < 1) {
    return showToast('Bitte Anzahl Gäste eingeben.', 'error');
  }
  if (!f.durationMin || f.durationMin < 15) {
    return showToast('Verweildauer muss mindestens 15 Minuten sein.', 'error');
  }
  // S134 P2 — frischen Walk-In-Picker-State aufsetzen (neue „Jetzt"-Zeit +
  // aktuelle Gästezahl/Dauer), dann den Picker als Step 3 rendern.
  setWalkInPickerState(buildWalkInCtx());
  _state.step = 3;
  rerender();
}

export function adminOrderBackToDineInData() {
  _state.step = 2;
  rerender();
}

/** S134 P2 — Picker-Accept (Vorschlag oder manuell): gewählte Tische in den
 *  Order-Entry-State übernehmen und zum Decision-Step. tables[0] ist der
 *  primäre Occupancy-Anker, assignedTableIds die volle Kombi. */
export function commitWalkInTableAssignment(tables, _chairs) {
  if (!Array.isArray(tables) || !tables.length) return;
  _state.assignedTableIds = tables.slice();
  _state.tableId = tables[0];
  _state.step = 4;
  rerender();
}

/** S134 P2 — „← Zurück" aus dem Picker: zurück zu den Gast-Daten (Step 2). */
export function adminOrderWalkInPickerBack() {
  _state.step = 2;
  rerender();
}

export function adminOrderBackToDineInTable() {
  _state.step = 3;
  rerender();
}

/** Decision: „Jetzt Bestellung aufnehmen" → Items-Step. */
export function adminOrderDineInProceedToOrder() {
  _state.notes = _state.dineInForm.notes || '';
  _state.step = 5;
  rerender();
}

/** Decision: „Erst Platz nehmen" → submit als Walk-In, modal schließt. */
export async function submitDineInSeatOnly() {
  // S60 W4.4 — Permission-Gate: walkin.create
  if (typeof window !== 'undefined' && window.App && window.App.auth
      && typeof window.App.auth.can === 'function'
      && !window.App.auth.can('walkin.create')) {
    if (typeof window.showToast === 'function') {
      window.showToast('Für diese Aktion fehlt dir die Berechtigung.', 'error');
    }
    return;
  }
  const state = getState();
  const table = (state.tables || []).find(t => t.id === _state.tableId);
  const f = _state.dineInForm;
  if (!window.App.storage.tableOccupancies || typeof window.App.storage.tableOccupancies.create !== 'function') {
    return showToast('Walk-In-Adapter fehlt — Cockpit neu laden.', 'error');
  }

  // S61 FIX.7 — Doppel-Submit-Schutz vor create_table_occupancy.
  // Operator entscheidet bei Konflikt explizit, ob er eine zweite Belegung
  // anlegt (Sharing) oder abbricht. Verhindert versehentliche
  // Duplikat-Belegungen aus doppeltem Klick / Re-Submit.
  try {
    const conflict = await window.App.storage.tableOccupancies.checkConflict(_state.tableId);
    if (conflict && conflict.has_conflict && conflict.occupancy) {
      const occ = conflict.occupancy;
      const min = Math.round(Number(occ.minutes_since_start) || 0);
      const tableName = (table && table.name) || _state.tableId;
      const guest = occ.guest_name ? ` (${occ.guest_name})` : '';
      const msg = `⚠️ Tisch ${tableName} ist BEREITS belegt: ${occ.guests} Gäste${guest}, belegt seit ${min} Min.`;
      if (!(await window.styledConfirm({ title: 'Trotzdem zweite Belegung (Sharing)?', body: msg, danger: true, confirmLabel: 'Sharing anlegen' })).confirmed) {
        // Operator bricht ab — Modal bleibt offen, kein Toast nötig.
        return;
      }
    }
  } catch (checkErr) {
    // Fail-safe: bei DB-Fehler nicht hart blockieren, RPC selbst hat audit_log
    console.warn('[submitDineInSeatOnly] conflict-check fehlgeschlagen, fahre fort:', checkErr);
  }

  // S61 W4.8 — Conflict-Warnung Walk-In ↔ Reservation. Wenn auf dem
  // Tisch in den nächsten 60 Min eine bestätigte/anstehende Reservierung
  // einläuft, Operator warnen — er hat dann typischerweise nur ~30-45 Min
  // bis der Tisch wieder frei sein muss.
  try {
    const now = new Date();
    const horizonMs = 60 * 60 * 1000; // 60 Min Vorausschau
    const todayIso = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
    const conflictRes = (state.reservations || []).find(r => {
      if (r.deletedAt) return false;
      if (r.status === 'declined' || r.status === 'cancelled' || r.status === 'no-show') return false;
      if (r.date !== todayIso) return false;
      if (!(r.assignedTables || []).includes(_state.tableId)) return false;
      // r.time z.B. "19:30"
      const [hh, mm] = (r.time || '00:00').split(':').map(Number);
      const resStartMs = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hh, mm).getTime();
      const deltaMs = resStartMs - now.getTime();
      return deltaMs > 0 && deltaMs <= horizonMs;
    });
    if (conflictRes) {
      const tableName = (table && table.name) || _state.tableId;
      const resTime = conflictRes.time;
      const resGuests = conflictRes.guests;
      const resName = conflictRes.name || 'Gast';
      const minutesUntil = Math.round((new Date(now.getFullYear(), now.getMonth(), now.getDate(), ...(conflictRes.time||'0:0').split(':').map(Number)).getTime() - now.getTime()) / 60000);
      const msg = `⚠️ Reservierung steht an für Tisch ${tableName}: ${resTime} Uhr · ${resGuests} Personen · ${resName} (in ${minutesUntil} Min). Der Walk-In sollte nur so lange dauern, dass der Tisch rechtzeitig frei ist.`;
      if (!(await window.styledConfirm({ title: 'Trotzdem belegen?', body: msg, danger: true, confirmLabel: 'Trotzdem belegen' })).confirmed) {
        return;
      }
    }
  } catch (resCheckErr) {
    console.warn('[submitDineInSeatOnly] reservation-conflict-check fehlgeschlagen, fahre fort:', resCheckErr);
  }

  // S134 P2 — Mehr-Tisch-Walk-In: assignedTableIds aus dem Picker, tableId
  // als primärer Anker. Fallback auf [tableId] für Single-Tisch.
  const assignedTableIds = (_state.assignedTableIds && _state.assignedTableIds.length)
    ? _state.assignedTableIds.slice() : [_state.tableId];
  let created;
  try {
    created = await window.App.storage.tableOccupancies.create({
      tableId:          _state.tableId,
      assignedTableIds: assignedTableIds,
      guests:           f.guests,
      guestName:        f.guestName || null,
      guestPhone:       f.guestPhone || null,
      durationMin:      f.durationMin,
      notes:            f.notes || null,
      customerId:       f.customerId || null
    });
  } catch (err) {
    console.error('[submitDineInSeatOnly]', err);
    return showToast('Konnte Walk-In nicht anlegen: ' + (err.message || err), 'error');
  }
  try { await window.App.events.emit('walkin:created', created); } catch (_) {}
  const tableName = (table && table.name) || _state.tableId;
  showToast(`Tisch ${tableName} belegt (${f.guests}P · ${f.durationMin} Min). Bestellung später über den Tisch aufnehmen.`, 'success');
  if (typeof window.closeAdminOrderEntry === 'function') window.closeAdminOrderEntry();
}

// Legacy-Stubs (Backward-Compat — Inline-Hint in alten Builds wird nicht mehr gerendert,
// aber Tests/Console-Aufrufe könnten noch existieren)
/** @deprecated S134 P2 — der Picker committet direkt via commitWalkInTableAssignment
 *  zum Decision-Step. Stub bleibt für Alt-Referenzen/Import-Stabilität. */
export function adminOrderDineInProceedToDecision() {
  if (!_state.tableId) {
    return showToast('Bitte einen Tisch wählen.', 'error');
  }
  _state.step = 4;
  rerender();
}
export function adminOrderEntrySeatOnly() {
  // Im neuen Flow ersetzt durch Step 4 Decision (submitDineInSeatOnly).
  return submitDineInSeatOnly();
}
export function adminOrderSetSeatField(f, v) { return adminOrderSetDineInField(f, v); }
export function adminOrderSeatLookupCustomer() { return adminOrderDineInLookupCustomer(); }
export function submitSeatOnly() { return submitDineInSeatOnly(); }
